<script>
/**
 * 収運・処分業者データ補完画面
 */
import {mapActions} from "vuex";
import Confirm from "../../../../components/Confirm.vue";

export default {
  name: "TraderComplementModal",
  components: {Confirm},
  props: {
    dialog: {
      type: Boolean,
      default: () => false
    },
    /**
     * 一覧で選択されたマニフェストID
     */
    selectableIds: {
      type: Array,
      default: () => []
    },
    searchedAt: {
      type: Number,
      default: () => null,
    },
  },
  watch: {
    /**
     * Dialog open/close event handler
     * @param {boolean} val
     */
    dialog(val) {
      this.transporters = [];
      this.disposers = [];
      if (val) {
        this.searchTraderComplements();
      }
    }
  },
  data () {
    return {
      /* 収運業者 */
      transporters: [],
      /* 処分業者 */
      disposers: [],
      enableComplementErrorMessageDialog: false,
      complementErrorMessages: [],
    }
  },
  methods: {
    ...mapActions("DriverTransferTraderComplement", {
      fetchTraderComplements: "index",
    }),
    ...mapActions("DriverTransferTrader", {
      createTraders: "create",
    }),
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit('closeCallback')
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      let modalOption = {
        title: '確認',
        message: '登録・紐付けをします。よろしいですか？',
        options: {width: 320, isCancelButton: true}
      }

      const parameters = this.buildParameters()
      if (parameters.transporters.length <= 0 && parameters.disposers.length <= 0) {
        return false;
      }

      this.$refs.traderComplementFormValidator.validate().then((result) => {
        if (result) {
          this.$refs.confirm.open(modalOption).then((confirm) => {
            this.$refs.confirm.close()
            if (confirm) {
              this.register(parameters);
            }
          })
        }
      })
    },
    dialogCloseCallback () {
      this.enableComplementErrorMessageDialog = false;
    },
    /**
     * 収運・処分業者データ取得
     */
    searchTraderComplements () {
      const parameters = {
        searched_at: this.searchedAt,
        jwnet_manifest_ids: this.selectableIds.join(',')
      }
      this.fetchTraderComplements({params: parameters}).then((content) => {
        this.transporters = content.transporters
        this.disposers = content.disposers
      })
    },
    /**
     * 登録で送信するパラメータを生成.
     * 公開確認番号未登録はスキップ
     */
    buildParameters () {
      let _transporters = [];
      let _disposers = [];
      for (const transporter of this.transporters) {
        if (transporter.confirmation_number) {
          _transporters.push({
            subscriber_number: transporter.subscriber_number,
            confirmation_number: transporter.confirmation_number
          })
        }
      }
      for (const disposer of this.disposers) {
        if (disposer.confirmation_number) {
          _disposers.push({
            subscriber_number: disposer.subscriber_number,
            confirmation_number: disposer.confirmation_number
          })
        }
      }
      return {
        jwnet_manifest_ids: [],
        searched_at: this.searchedAt,
        transporters: _transporters,
        disposers: _disposers,
      }
    },
    /**
     * 登録処理
     * 入力内容に不備がある場合は
     * 入力内容に不備がない場合は本モーダルを閉じる
     * @param parameters
     */
    register (parameters) {
      this.createTraders({params: parameters}).then((content) => {
        if (content?.error_messages) {
          this.searchTraderComplements();
          this.complementErrorMessages = content?.error_messages;
          this.enableComplementErrorMessageDialog = true;
          this.$emit("registerCallback");
        } else {
          this.$emit("registerCallback");
          this.closeButtonClickEvent()
        }
      })
    }
  }
}
</script>

<template>
  <div>
    <validation-observer ref="traderComplementFormValidator">
      <v-dialog
        :value="dialog"
        scrollable
        width="1500"
        @click:outside="closeButtonClickEvent"
        @close="closeButtonClickEvent"
        @keydown.esc="closeButtonClickEvent"
      >
        <v-card>
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title>
              収運・処分業者データ補完
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                dark
                @click="closeButtonClickEvent"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-text>
            <v-row
              v-if="transporters.length <= 0 && disposers.length <= 0"
            >
              <v-col>
                <v-card flat>
                  <v-card-title class="primary--text">収運業者</v-card-title>
                  <v-card-text>
                    <p class="font-weight-thin text-center">データはありません。</p>
                  </v-card-text>
                </v-card>
                <v-divider />
                <v-card flat>
                  <v-card-title class="primary--text">処分業者</v-card-title>
                  <v-card-text>
                    <p class="font-weight-thin text-center">データはありません。</p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-if="transporters.length > 0" dense>
              <v-col>
                <v-card flat>
                  <v-card-title class="primary--text">収運業者</v-card-title>
                  <v-card-text>
                    <v-container fluid>
                      <v-row
                        v-for="(transporter, i) in transporters"
                        :key="`trader-${i}`"
                        dense
                      >
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <h4>{{ transporter.name }}</h4>
                          <div>
                            〒{{ transporter.zip_code }}<br />
                            <small>{{ transporter.address }}</small>
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <div style="width: 80%">
                            <v-text-field
                              :value="transporter.subscriber_number"
                              label="加入者番号"
                              hide-details="auto"
                              dense
                              readonly
                            />
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            name="公開確認番号"
                            rules="numeric|length: 6"
                            :vid="`trader_${i}`"
                          >
                            <v-text-field
                              v-model="transporter.confirmation_number"
                              :error-messages="errors"
                              dense
                              label="公開確認番号"
                              hide-details="auto"
                              outlined
                              persistent-placeholder
                              placeholder="例）111111"
                            >
                              <template v-slot:label>
                                <span>公開確認番号</span>
                                <span class="ml-1">
                                  ({{ transporter.confirmation_number?.length || 0 }} / 6)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-divider v-if="transporters.length > 0 && disposers.length > 0" />
            <v-row v-if="disposers.length > 0" dense>
              <v-col>
                <v-card flat>
                  <v-card-title class="primary--text">処分業者</v-card-title>
                  <v-card-text>
                    <v-container fluid>
                      <v-row
                        v-for="(disposer, i) in disposers"
                        :key="`disposer-${i}`"
                        dense
                      >
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <h4>{{ disposer.name }}</h4>
                          <div>
                            <div>
                              〒{{ disposer.zip_code }}<br />
                              <small>{{ disposer.address }}</small>
                            </div>
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <div style="width: 80%">
                            <v-text-field
                              :value="disposer.subscriber_number"
                              label="加入者番号"
                              hide-details="auto"
                              dense
                              readonly
                            />
                          </div>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="4"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            name="公開確認番号"
                            rules="numeric|length: 6"
                            :vid="`disposer_${i}`"
                          >
                            <v-text-field
                              v-model="disposer.confirmation_number"
                              :error-messages="errors"
                              dense
                              label="公開確認番号"
                              hide-details="auto"
                              outlined
                              persistent-placeholder
                              placeholder="例）111111"
                            >
                              <template v-slot:label>
                                <span>公開確認番号</span>
                                <span class="ml-1">
                                  ({{ disposer.confirmation_number?.length || 0 }} / 6)
                                </span>
                              </template>
                              <template v-slot:counter></template>
                            </v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn
              @click="closeButtonClickEvent"
            >
              閉じる
            </v-btn>
            <v-btn
              :disabled="transporters.length <= 0 && disposers.length <= 0"
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>

          </v-card-actions>
        </v-card>
      </v-dialog>
    </validation-observer>

    <confirm ref="confirm" />
    <v-dialog
      v-model="enableComplementErrorMessageDialog"
      width="700"
      scrollable
      @click:outside="closeButtonClickEvent"
      @close="dialogCloseCallback"
      @keydown.esc="dialogCloseCallback"
    >
      <v-card>
        <v-toolbar
          color="error"
          dark
          flat
        >
          <v-toolbar-title>
            確認
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              dark
              @click="dialogCloseCallback"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-subtitle class="mt-4">
          <h3>
            以下はスキップされました。
          </h3>
        </v-card-subtitle>
        <v-card-text style="max-height: 300px;">
          <ul v-if="complementErrorMessages">
            <li
              v-for="(errorMessage, index) in complementErrorMessages"
              :key="index"
              v-html="errorMessage"
            />
          </ul>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="dialogCloseCallback"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">

</style>
